import { useLocation, useNavigate } from 'react-router-dom';
import { useCallback, useEffect, useMemo } from 'react';
import { CustomerType, PluginSettings, ProductType, ShopType } from 'gql/__generated/vp-apollo';
import { useCreateSession } from './useCreateSession';

const useRoutePathForCustomerType = (pluginSettings: PluginSettings) => {
    const { pathname } = useLocation();

    const pathForCustomerType = useMemo(() => {
        const { customerType } = pluginSettings;

        if (!customerType && (/^\/bestandskunden/.test(pathname) || /^\/neukunden/.test(pathname))) {
            return '/';
        }

        if (
            (customerType === CustomerType.New || customerType === CustomerType.ProviderChange) &&
            !/^\/neukunden/.test(pathname)
        ) {
            return '/neukunden';
        }

        if (customerType === CustomerType.Existing && !/^\/bestandskunden/.test(pathname)) {
            return '/bestandskunden';
        }
    }, [pluginSettings, pathname]);

    return { pathForCustomerType };
};

const useSessionInput = (pluginSettings: PluginSettings) => {
    return useMemo(() => {
        const { customerType, sellerId, productType, isYoung = false } = pluginSettings;

        return {
            vbIdentifier: sellerId,
            sessionContext: {
                productType,
                customerType,
                ...(productType === ProductType.Fttx ? { isYoung } : {}),
                shopType: ShopType.Webshop,
            },
        };
    }, [pluginSettings]);
};

const useProvideSessionOrRedirect = (pluginSettings: PluginSettings) => {
    const navigate = useNavigate();

    const { createSession, loading, called, error } = useCreateSession();
    const { pathForCustomerType } = useRoutePathForCustomerType(pluginSettings);
    const sessionInput = useSessionInput(pluginSettings);

    const provideSessionOrRedirect = useCallback(() => {
        createSession(sessionInput);
        if (pathForCustomerType) {
            navigate(pathForCustomerType, { replace: true });
        }
    }, [createSession, sessionInput, pathForCustomerType, navigate]);

    return { provideSessionOrRedirect, loading, called, error };
};

export const useProvideSessionOrRedirectEffect = (pluginSettings: PluginSettings) => {
    const {
        provideSessionOrRedirect,
        loading,
        called: sessionFetched,
        error,
    } = useProvideSessionOrRedirect(pluginSettings);

    useEffect(() => {
        if (!sessionFetched) {
            provideSessionOrRedirect();
        }
    }, [sessionFetched, provideSessionOrRedirect]);

    return { loading, error, called: sessionFetched };
};
